import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Keyboard, KeyboardAvoidingView, Platform, StyleSheet } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useNavigation } from '@react-navigation/native';

import IconCircledClose from '@/assets/icons/circled-close';
import DollarSignIcon from '@/assets/icons/dollar';
import { Button } from '@/components/ButtonComponent';
import { SheetScreen } from '@/components/ScreenComponent';
import { ScreenNavBar } from '@/components/ScreenNavBar';
import { Text } from '@/components/TextComponent';
import { TextInput } from '@/components/TextInput';
import { Box, TouchableBox } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { common, designSystem } from '@/styles/styles';

import { useQuickAmounts } from '../hooks/use-quick-amounts';
import { QuickButton, useUserQuickAmounts } from '../hooks/use-user-quick-amounts-store';

type QuickButtonId = QuickButton['id'];

const MAX_DEFAULT_VALUE = 2000;
const MIN_DEFAULT_VALUE = 1;

export const EditQuickAmounts = () => {
    const navigation = useNavigation();
    const { t } = useTranslation(['betslip_pickem', 'common']);
    const quickAmounts = useQuickAmounts();
    const { setQuickAmounts } = useUserQuickAmounts(state => state.actions);

    const insets = useSafeAreaInsets();

    const [amounts, setAmounts] = useState(quickAmounts.map(el => ({ ...el, hasError: false, errorMessage: '' })));

    const handleClearButton = (id: QuickButtonId) => {
        const changedAmounts = amounts.map(el => {
            if (el.id === id) {
                return { ...el, value: null, hasError: true };
            }
            return { ...el };
        });

        setAmounts(changedAmounts);
    };

    const checkForErrors = (
        value: number | null,
        id: QuickButtonId,
        newValue: number | null,
        changedId: QuickButtonId
    ) => {
        if (value === null) {
            return { hasError: true, errorMessage: '' };
        }

        if (value > MAX_DEFAULT_VALUE) {
            return { hasError: true, errorMessage: t('quick_amounts_exceed_error', { maxValue: MAX_DEFAULT_VALUE }) };
        }

        if (value < MIN_DEFAULT_VALUE) {
            return { hasError: true, errorMessage: t('quick_amounts_less_error', { minValue: MIN_DEFAULT_VALUE }) };
        }

        // this check is for the input that is changing
        if (id === changedId) {
            if (amounts.some(el => el.id !== id && el.value === value)) {
                return { hasError: true, errorMessage: t('quick_amounts_duplicates_error') };
            }
            return { hasError: false, errorMessage: '' };
        }

        const duplicateFound = amounts.some(el => {
            return (
                //exclude current element (since the values will always match) from search
                //and exclude the element that has been changed since the value is not updated yet
                (el.id !== id && el.id !== changedId && el.value === value) ||
                //check if the element that has been changed has the same value with the current element in list
                (el.id === changedId && newValue === value)
            );
        });

        if (duplicateFound) {
            return { hasError: true, errorMessage: '' };
        }

        return { hasError: false, errorMessage: '' };
    };

    const onValueChange = (value: string, id: QuickButtonId) => {
        //for web in case the user tries to type a letter
        if (isNaN(Number(value))) {
            return;
        }

        //for web and android where you can have decimal values
        const newValue = value ? Number(Number(value).toFixed(2)) : null;

        const changedAmounts = amounts.map(el => {
            const { hasError, errorMessage } = checkForErrors(el.id === id ? newValue : el.value, el.id, newValue, id);
            return { ...el, value: el.id === id ? newValue : el.value, hasError, errorMessage };
        });

        setAmounts(changedAmounts);
    };

    const isDataValid = useMemo(() => {
        const hasErrors = amounts.some(el => el.hasError);
        if (hasErrors) {
            return false;
        }

        const hasChanges = amounts.some((el, index) => el.value !== quickAmounts[index].value);
        return hasChanges;
    }, [amounts, quickAmounts]);

    const handleSave = () => {
        //On android the keyboard creates an issue on EntryInput. It pushes all the content up and it overlaps
        Keyboard.dismiss();
        let dummyAmounts = [...amounts].sort((a, b) => (a.value ?? 0) - (b.value ?? 0));

        setQuickAmounts(dummyAmounts);

        navigation.goBack();
    };

    return (
        <SheetScreen style={common.flex} edges={['top']}>
            <Box flex={1} backgroundColor={ds.colorToken.background.neutral.base}>
                <ScreenNavBar
                    sheet
                    title={t('quick_amounts')}
                    closeIconMode={'close'}
                    titleOverrideStyle={styles.title}
                />
                <ScrollView keyboardShouldPersistTaps="handled" contentContainerStyle={styles.scroll}>
                    <KeyboardAvoidingView
                        keyboardVerticalOffset={Platform.OS === 'ios' ? 90 : 0}
                        behavior={Platform.OS === 'ios' ? 'position' : undefined}
                        style={common.flex}
                    >
                        <Box paddingHorizontal="s16">
                            <Box gap="s16" marginBottom="s24">
                                <Text variant="bodySmall" letterSpacing={-0.08} color={'neutral300'}>
                                    {t('quick_amounts_info')}
                                </Text>
                                <Text variant="bodySmall" letterSpacing={-0.08} color={'neutral300'}>
                                    {t('quick_amounts_limitations', {
                                        maxValue: MAX_DEFAULT_VALUE,
                                        minValue: MIN_DEFAULT_VALUE,
                                    })}
                                </Text>
                            </Box>

                            <Box gap="s16">
                                {amounts.map((el, index) => (
                                    <TextInput
                                        key={el.id}
                                        hasError={el.hasError}
                                        errorText={el.errorMessage}
                                        icon={<DollarSignIcon />}
                                        label={`${t('quick_amount')} ${index + 1}`}
                                        keyboardType="number-pad"
                                        rightIcon={
                                            <ClearInputValueIcon handleClearButton={() => handleClearButton(el.id)} />
                                        }
                                        value={el.value?.toString() ?? ''}
                                        onChangeText={(masked: string) => onValueChange(masked, el.id)}
                                        returnKeyType="done"
                                        selectionColor={ds.colorValue.text.brand}
                                        selectionHandleColor={ds.colorValue.text.brand}
                                    />
                                ))}
                            </Box>
                        </Box>
                    </KeyboardAvoidingView>
                </ScrollView>
                <Box
                    style={{ marginBottom: insets.bottom + 16 }}
                    position={'absolute'}
                    px={'s16'}
                    bottom={0}
                    left={0}
                    right={0}
                >
                    <Button
                        label={t('common:update')}
                        style={styles.button}
                        textStyle={styles.buttonText}
                        disableContainerStyle={styles.disabledButton}
                        disableTextStyle={styles.disabledButtonText}
                        hierarchy={'primary'}
                        disabled={!isDataValid}
                        onPress={handleSave}
                    />
                </Box>
            </Box>
        </SheetScreen>
    );
};

type ClearButtonProps = {
    handleClearButton: () => void;
};

const ClearInputValueIcon = ({ handleClearButton }: ClearButtonProps) => {
    return (
        <TouchableBox onPress={handleClearButton} style={styles.downArrow}>
            <IconCircledClose />
        </TouchableBox>
    );
};

const styles = StyleSheet.create({
    downArrow: {
        position: 'absolute',
        top: 20,
        right: 22,
        marginRight: -10,
    },
    title: {
        color: ds.colorValue.text.primary,
    },
    scroll: {
        marginTop: 8,
    },
    button: {
        borderRadius: 14,
        paddingVertical: 12,
        borderCurve: 'continuous',
    },
    buttonText: {
        color: designSystem.colors.neutral900,
        fontSize: 16,
        letterSpacing: -0.31,
    },
    disabledButton: {
        backgroundColor: ds.colorValue.background.neutral.subtler,
    },
    disabledButtonText: {
        color: designSystem.colors.neutral400,
    },
});
